import { useEffect, useRef, useState } from "react";
import { VOICE_NOTE_MAX_MS } from "@family/shared";
import { canRecordAudio, pickRecordingMime } from "../../lib/voiceNotes";

export interface Recording {
  blob: Blob;
  durationMs: number;
}

interface VoiceRecorderProps {
  value: Recording | null;
  onChange: (r: Recording | null) => void;
}

function clock(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Tap to record a spoken story, tap again to stop; stops by itself at the limit. */
export function VoiceRecorder({ value, onChange }: VoiceRecorderProps) {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const startedAt = useRef(0);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(value.blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [value]);

  useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => {
      const ms = Date.now() - startedAt.current;
      setElapsed(ms);
      if (ms >= VOICE_NOTE_MAX_MS) recorderRef.current?.stop();
    }, 250);
    return () => window.clearInterval(id);
  }, [recording]);

  // Release the microphone if the user leaves mid-recording.
  useEffect(() => () => recorderRef.current?.stream.getTracks().forEach((t) => t.stop()), []);

  async function start() {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Microphone access was refused.");
      return;
    }
    const mimeType = pickRecordingMime();
    const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
      const durationMs = Math.min(Date.now() - startedAt.current, VOICE_NOTE_MAX_MS);
      const blob = new Blob(chunks, { type: (rec.mimeType || mimeType || "audio/webm").split(";")[0] });
      if (blob.size > 0) onChange({ blob, durationMs });
    };
    recorderRef.current = rec;
    startedAt.current = Date.now();
    setElapsed(0);
    rec.start(1000);
    setRecording(true);
  }

  if (!canRecordAudio()) {
    return <p className="text-subtle">This browser can't record audio.</p>;
  }

  return (
    <div className="scan-voice">
      {recording ? (
        <button type="button" className="scan-voice__btn is-recording" onClick={() => recorderRef.current?.stop()}>
          ■ Stop · {clock(elapsed)} / {clock(VOICE_NOTE_MAX_MS)}
        </button>
      ) : value && url ? (
        <div className="scan-voice__done">
          <audio src={url} controls />
          <button type="button" className="btn-tertiary" onClick={() => onChange(null)}>
            Delete
          </button>
        </div>
      ) : (
        <button type="button" className="btn-secondary scan-voice__btn" onClick={() => void start()}>
          🎙 Record the story
        </button>
      )}
      {error && <p className="text-error">{error}</p>}
    </div>
  );
}
